import React, { useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes
} from 'react-router-dom';
import AppHeader from './components/AppHeader';
import {
  initialCurriculaByChild,
  student
} from './data/mockData';
import useAppAuth from './hooks/useAppAuth';
import Auth from './pages/Auth';
import Onboarding from './pages/Onboarding';
import ParentDashboard from './pages/ParentDashboard';
import Setup from './pages/Setup';
import StudentTutor from './pages/StudentTutor';
import './App.css';
import './quantumTheme.css';

const onboardingStorageKey = 'brightpath-onboarding-complete';

function updateChildLesson(
  curricula,
  childId,
  lessonId,
  updates
) {
  return {
    ...curricula,
    [childId]: curricula[childId].map((group) => ({
      ...group,
      lessons: group.lessons.map((lesson) => (
        lesson.id === lessonId
          ? {
            ...lesson,
            ...(typeof updates === 'function'
              ? updates(lesson)
              : updates)
          }
          : lesson
      ))
    }))
  };
}

function LoadingScreen({ message = 'Loading Quantix…' }) {
  return (
    <div className="app-container flex min-h-screen items-center justify-center">
      <p className="text-sm font-semibold text-[#6c7773]">{message}</p>
    </div>
  );
}

function AccountErrorScreen({ message, onRetry }) {
  return (
    <div className="app-container flex min-h-screen flex-col items-center justify-center gap-4 px-6">
      <p className="max-w-md text-center text-sm text-[#ba503c]">{message}</p>
      <button
        className="rounded-xl bg-[#7c3aed] px-5 py-3 text-sm font-semibold text-white"
        onClick={onRetry}
        type="button"
      >
        Try again
      </button>
    </div>
  );
}

function AuthenticatedApp({ user, role, householdId, studentChild }) {
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [curriculaByChild, setCurriculaByChild] = useState(
    initialCurriculaByChild
  );

  const updateLesson = (childId, lessonId, updates) => {
    setCurriculaByChild((current) => (
      updateChildLesson(current, childId, lessonId, updates)
    ));
  };

  const updateStudentLesson = (lessonId, updates) => {
    updateLesson(student.id, lessonId, updates);
  };

  const completeLesson = (lessonId) => {
    setCurriculaByChild((current) => ({
      ...current,
      [student.id]: current[student.id].map((group) => {
        const lessonIndex = group.lessons.findIndex(
          (lesson) => lesson.id === lessonId
        );

        if (lessonIndex === -1) return group;

        return {
          ...group,
          lessons: group.lessons.map((lesson, index) => {
            if (lesson.id === lessonId) {
              return {
                ...lesson,
                status: 'completed',
                progress: 100
              };
            }

            if (
              index === lessonIndex + 1
              && lesson.status === 'locked'
            ) {
              return {
                ...lesson,
                status: 'available',
                progress: 0
              };
            }

            return lesson;
          })
        };
      })
    }));
  };

  const completeOnboarding = () => {
    window.localStorage.setItem(onboardingStorageKey, 'true');
  };

  const studentCurriculum = curriculaByChild[student.id];
  const isStudent = role === 'student';

  return (
    <div className="app-container">
      <AppHeader role={role} studentChild={studentChild} user={user} />

      <Routes>
        <Route
          element={
            isStudent
              ? (
                <StudentTutor
                  curriculum={studentCurriculum}
                  onCompleteLesson={completeLesson}
                  onUpdateLesson={updateStudentLesson}
                />
              )
              : <Navigate replace to="/parent" />
          }
          path="/"
        />

        <Route
          element={
            isStudent
              ? <Navigate replace to="/" />
              : <ParentDashboard householdId={householdId} />
          }
          path="/parent"
        />

        <Route
          element={
            isStudent
              ? <Navigate replace to="/" />
              : (
                <Onboarding
                  curriculum={studentCurriculum}
                  onboardingStep={onboardingStep}
                  onComplete={completeOnboarding}
                  onStepChange={setOnboardingStep}
                />
              )
          }
          path="/onboarding"
        />

        <Route
          element={<Navigate replace to={isStudent ? '/' : '/parent'} />}
          path="*"
        />
      </Routes>
    </div>
  );
}

function AppRoutes() {
  const {
    session,
    authReady,
    accountStatus,
    accountRole,
    householdId,
    studentChild,
    accountError,
    refreshAccount
  } = useAppAuth();

  if (!authReady) {
    return <LoadingScreen />;
  }

  if (!session) {
    return (
      <Routes>
        <Route element={<Auth />} path="/auth" />
        <Route element={<Navigate replace to="/auth" />} path="*" />
      </Routes>
    );
  }

  if (accountStatus === 'error') {
    return (
      <AccountErrorScreen
        message={accountError}
        onRetry={refreshAccount}
      />
    );
  }

  if (accountStatus === 'needs-setup') {
    return (
      <Routes>
        <Route
          element={(
            <Setup
              existingHouseholdId={householdId}
              userId={session.user.id}
              onComplete={refreshAccount}
            />
          )}
          path="/setup"
        />
        <Route element={<Navigate replace to="/setup" />} path="*" />
      </Routes>
    );
  }

  if (accountStatus !== 'ready') {
    return <LoadingScreen />;
  }

  return (
    <AuthenticatedApp
      householdId={householdId}
      role={accountRole}
      studentChild={studentChild}
      user={session.user}
    />
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
